<script setup>
import { nextTick, ref } from 'vue';
import { useRouter } from 'vue-router';
import nexus from '@/service/nexus';
import { useRecentCases } from '@/composables/useRecentCases';

const router = useRouter();
const { recentCases, loadRecentCases, addRecentCase } = useRecentCases();

const pickerPopover = ref(null);
const searchInput = ref(null);
const searchQuery = ref('');
const searchResults = ref([]);
const loading = ref(false);
const errorMessage = ref('');
const hasSearched = ref(false);

const getStatusSeverity = (status) => {
    switch (status) {
        case 'Active':
            return 'success';
        case 'Closed':
            return 'danger';
        case 'Dormant':
            return 'warning';
        default:
            return 'info';
    }
};

const getCaseTypeSeverity = (caseType) => {
    switch (caseType) {
        case 'MVA':
            return 'info';
        case 'PI':
            return 'warning';
        case 'WC':
            return 'danger';
        case 'AUT':
            return 'success';
        case 'CON':
            return 'primary';
        default:
            return 'secondary';
    }
};

const focusSearchInput = async () => {
    await nextTick();

    const inputElement = searchInput.value?.$el?.querySelector?.('input') || searchInput.value?.$el || searchInput.value;
    inputElement?.focus?.();
};

const clearSessionState = () => {
    searchQuery.value = '';
    searchResults.value = [];
    loading.value = false;
    errorMessage.value = '';
    hasSearched.value = false;
};

const togglePicker = (event) => {
    pickerPopover.value?.toggle(event);
};

const onPickerShow = async () => {
    loadRecentCases();
    await focusSearchInput();
};

const onPickerHide = () => {
    clearSessionState();
};

const searchCases = async () => {
    const query = searchQuery.value.trim();

    if (!query) {
        hasSearched.value = false;
        searchResults.value = [];
        errorMessage.value = '';
        loadRecentCases();
        return;
    }

    try {
        loading.value = true;
        errorMessage.value = '';
        hasSearched.value = true;

        const response = await nexus.crm.getCases({
            search: query,
            page: 1,
            page_size: 8
        });

        searchResults.value = response.data.results || [];
    } catch (error) {
        console.error('Error searching cases from topbar picker:', error);
        errorMessage.value = error.response?.data?.detail || error.response?.data?.error || error.message || 'Failed to search cases';
        searchResults.value = [];
    } finally {
        loading.value = false;
    }
};

const selectCase = async (caseData) => {
    addRecentCase(caseData);
    pickerPopover.value?.hide();

    await router.push({
        name: 'crm-case-detail',
        params: { caseNumber: caseData.case_number }
    });
};
</script>

<template>
    <li class="right-sidebar-item topbar-case-search-item">
        <button type="button" class="topbar-case-trigger" @click="togglePicker" aria-label="Search cases" data-testid="case-picker-trigger">
            <i class="pi pi-search" />
            <span class="topbar-case-trigger-label">Case Search</span>
        </button>

        <Popover ref="pickerPopover" class="topbar-case-picker-popover" :pt="{ content: '!p-0' }" @show="onPickerShow" @hide="onPickerHide">
            <div class="topbar-case-panel">
                <div class="topbar-case-panel-header">
                    <div class="topbar-case-search-row">
                        <InputText ref="searchInput" v-model="searchQuery" placeholder="Search cases by name or number" class="w-full" data-testid="case-picker-input" @keydown.enter.prevent="searchCases" />
                        <Button type="button" icon="pi pi-arrow-right" label="Search" class="topbar-case-search-button" size="small" data-testid="case-picker-search-button" @click="searchCases" />
                    </div>
                </div>

                <div v-if="loading" class="topbar-case-panel-state" data-testid="case-picker-loading">
                    <ProgressSpinner strokeWidth="6" style="width: 2rem; height: 2rem" />
                    <p class="m-0 text-sm text-surface-500">Searching cases...</p>
                </div>

                <div v-else-if="errorMessage" class="topbar-case-panel-state" data-testid="case-picker-error">
                    <i class="pi pi-exclamation-circle text-xl text-red-500" />
                    <p class="m-0 text-sm text-surface-600">{{ errorMessage }}</p>
                </div>

                <div v-else-if="hasSearched" class="topbar-case-results">
                    <div class="topbar-case-results-header">
                        <span class="text-sm font-medium text-surface-700">Search Results</span>
                        <span class="text-xs text-surface-500">{{ searchResults.length }} found</span>
                    </div>

                    <div v-if="searchResults.length" class="topbar-case-list" data-testid="case-picker-results">
                        <button v-for="caseItem in searchResults" :key="caseItem.case_number" type="button" class="topbar-case-list-item" :data-testid="`case-picker-result-${caseItem.case_number}`" @click="selectCase(caseItem)">
                            <div class="topbar-case-list-copy">
                                <div class="font-semibold text-sm">{{ caseItem.case_number }}</div>
                                <div class="text-sm text-surface-700">{{ caseItem.name || 'Untitled case' }}</div>
                            </div>
                            <div class="topbar-case-list-meta">
                                <Tag :severity="getStatusSeverity(caseItem.status)" :value="caseItem.status || 'Unknown'" />
                                <Tag :severity="getCaseTypeSeverity(caseItem.case_type)" :value="caseItem.case_type || 'Unknown'" />
                            </div>
                        </button>
                    </div>

                    <div v-else class="topbar-case-panel-state" data-testid="case-picker-empty">
                        <i class="pi pi-search text-xl text-surface-400" />
                        <p class="m-0 text-sm text-surface-500">No cases found for "{{ searchQuery.trim() }}"</p>
                    </div>
                </div>

                <div v-else class="topbar-case-results">
                    <div class="topbar-case-results-header">
                        <span class="text-sm font-medium text-surface-700">Recent Cases</span>
                        <span class="text-xs text-surface-500">Quick access</span>
                    </div>

                    <div v-if="recentCases.length" class="topbar-case-list" data-testid="case-picker-recents">
                        <button v-for="caseItem in recentCases" :key="caseItem.case_number" type="button" class="topbar-case-list-item" :data-testid="`case-picker-recent-${caseItem.case_number}`" @click="selectCase(caseItem)">
                            <div class="topbar-case-list-copy">
                                <div class="font-semibold text-sm">{{ caseItem.case_number }}</div>
                                <div class="text-sm text-surface-700">{{ caseItem.name || 'Untitled case' }}</div>
                            </div>
                            <div class="topbar-case-list-meta">
                                <Tag :severity="getStatusSeverity(caseItem.status)" :value="caseItem.status || 'Unknown'" />
                                <Tag :severity="getCaseTypeSeverity(caseItem.case_type)" :value="caseItem.case_type || 'Unknown'" />
                            </div>
                        </button>
                    </div>

                    <div v-else class="topbar-case-panel-state" data-testid="case-picker-no-recents">
                        <i class="pi pi-clock text-xl text-surface-400" />
                        <p class="m-0 text-sm text-surface-500">Search for a case to start building your recent list.</p>
                    </div>
                </div>
            </div>
        </Popover>
    </li>
</template>

<style scoped>
.topbar-case-trigger {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    height: 2.5rem;
    padding: 0 0.875rem;
    border: 1px solid var(--surface-border);
    border-radius: 999px;
    background: var(--surface-card);
    color: var(--text-color);
    cursor: pointer;
    transition:
        background-color var(--transition-duration),
        border-color var(--transition-duration),
        box-shadow var(--transition-duration);
}

.topbar-case-trigger:hover {
    background: var(--surface-hover);
}

.topbar-case-trigger:focus-visible {
    box-shadow: var(--focus-ring-shadow);
    outline: none;
}

.topbar-case-trigger-label {
    font-size: 0.875rem;
    font-weight: 600;
    white-space: nowrap;
}

.topbar-case-panel {
    width: min(32rem, calc(100vw - 2rem));
    max-height: min(34rem, calc(100vh - 8rem));
    display: flex;
    flex-direction: column;
}

.topbar-case-panel-header {
    padding: 1rem;
    border-bottom: 1px solid var(--surface-border);
}

.topbar-case-search-row {
    display: flex;
    gap: 0.75rem;
    align-items: center;
}

.topbar-case-search-button {
    flex-shrink: 0;
}

.topbar-case-results {
    display: flex;
    flex-direction: column;
    min-height: 14rem;
}

.topbar-case-results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.875rem 1rem 0.5rem;
}

.topbar-case-list {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    overflow-y: auto;
    padding: 0 0.5rem 0.5rem;
}

.topbar-case-list-item {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
    padding: 0.875rem;
    border: none;
    border-radius: 0.875rem;
    background: transparent;
    cursor: pointer;
    text-align: left;
    transition: background-color var(--transition-duration);
}

.topbar-case-list-item:hover,
.topbar-case-list-item:focus-visible {
    background: var(--surface-hover);
    outline: none;
}

.topbar-case-list-copy {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
}

.topbar-case-list-meta {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.topbar-case-panel-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    min-height: 14rem;
    padding: 1rem;
    text-align: center;
}

@media screen and (max-width: 576px) {
    .topbar-case-trigger {
        width: 2.5rem;
        justify-content: center;
        padding: 0;
    }

    .topbar-case-trigger-label {
        display: none;
    }

    .topbar-case-panel {
        width: min(100vw - 2rem, 26rem);
        max-height: calc(100vh - 10rem);
    }

    .topbar-case-search-row {
        flex-direction: column;
        align-items: stretch;
    }

    .topbar-case-list-item {
        flex-direction: column;
    }

    .topbar-case-list-meta {
        justify-content: flex-start;
    }
}
</style>
